import { ArrowLeft, Calendar, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Loading } from '@/core/ui/components/Loading';
import { useIncomeExpense } from '../hooks';
import { ReportExportButton } from '../components';
import { formatCurrency } from '@yawi/infrastructure';

interface IncomeExpenseReportPageProps {
  onBack: () => void;
}

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const IncomeExpenseReportPage = ({ onBack }: IncomeExpenseReportPageProps) => {
  const { report, loading, exporting, params, setYear, setCompareYear, exportReport, currentYear } = useIncomeExpense();

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Calculate max value for chart scaling
  const maxValue = report ? Math.max(
    ...report.incomeByMonth.map(m => m.totalVerified),
    ...report.expensesByMonth.map(m => m.total)
  ) : 0;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Ingresos y Egresos</h2>
            <p className="text-slate-500 text-sm">Flujo de caja anual</p>
          </div>
        </div>

        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Año
            </label>
            <select
              value={params.year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Comparar con
            </label>
            <select
              value={params.compareYear || ''}
              onChange={(e) => setCompareYear(e.target.value ? parseInt(e.target.value) : undefined)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Sin comparación</option>
              {years.filter(y => y !== params.year).map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Loading />
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-emerald-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-emerald-600" />
                <span className="text-sm text-slate-600">Total Ingresos</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(report.summary.totalIncome)}</p>
              {report.incomeVariation !== undefined && (
                <div className={`flex items-center gap-1 text-sm ${report.incomeVariation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {report.incomeVariation >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {Math.abs(report.incomeVariation).toFixed(1)}% vs año anterior
                </div>
              )}
            </div>

            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={18} className="text-red-600" />
                <span className="text-sm text-slate-600">Total Egresos</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(report.summary.totalExpenses)}</p>
              {report.expenseVariation !== undefined && (
                <div className={`flex items-center gap-1 text-sm ${report.expenseVariation <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {report.expenseVariation >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {Math.abs(report.expenseVariation).toFixed(1)}% vs año anterior
                </div>
              )}
            </div>

            <div className={`rounded-xl p-4 ${report.summary.netBalance >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={18} className={report.summary.netBalance >= 0 ? 'text-blue-600' : 'text-amber-600'} />
                <span className="text-sm text-slate-600">Balance Neto</span>
              </div>
              <p className={`text-2xl font-bold ${report.summary.netBalance >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                {formatCurrency(report.summary.netBalance)}
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={18} className="text-slate-600" />
                <span className="text-sm text-slate-600">Mejor Mes (Ingresos)</span>
              </div>
              <p className="text-xl font-bold text-slate-700">{report.summary.highestIncomeMonth}</p>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Ingresos vs Egresos por Mes</h3>
            <div className="space-y-3">
              {report.monthlyBalance.map((month, index) => (
                <div key={month.month} className="flex items-center gap-4">
                  <div className="w-12 text-sm text-slate-600">{MONTHS[index]}</div>
                  <div className="flex-1 flex gap-1 h-8">
                    {/* Income Bar */}
                    <div
                      className="bg-emerald-500 rounded-l h-full transition-all"
                      style={{ width: `${maxValue ? (month.income / maxValue) * 50 : 0}%` }}
                      title={`Ingresos: ${formatCurrency(month.income)}`}
                    />
                    {/* Expense Bar */}
                    <div
                      className="bg-red-500 rounded-r h-full transition-all"
                      style={{ width: `${maxValue ? (month.expenses / maxValue) * 50 : 0}%` }}
                      title={`Egresos: ${formatCurrency(month.expenses)}`}
                    />
                  </div>
                  <div className={`w-24 text-right text-sm font-medium ${month.netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(month.netBalance)}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-6 mt-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-500 rounded"></div>
                <span className="text-slate-600">Ingresos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-slate-600">Egresos</span>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Income by Category */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Ingresos por Método de Pago</h3>
              <div className="space-y-3">
                {report.incomeByCategory.map((cat) => (
                  <div key={cat.category} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-700">{cat.category}</span>
                        <span className="text-slate-500">{cat.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${cat.percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-28 text-right text-sm font-medium text-slate-700">
                      {formatCurrency(cat.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expenses by Category */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Egresos por Categoría</h3>
              <div className="space-y-3">
                {report.expensesByCategory.map((cat) => (
                  <div key={cat.category} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-700">{cat.category}</span>
                        <span className="text-slate-500">{cat.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${cat.percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-28 text-right text-sm font-medium text-slate-700">
                      {formatCurrency(cat.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Comparison */}
          {report.comparison && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Comparación con {report.comparison.year}</h3>
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Ingresos {report.comparison.year}</div>
                  <div className="text-xl font-bold text-slate-700">{formatCurrency(report.comparison.totalIncome)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Egresos {report.comparison.year}</div>
                  <div className="text-xl font-bold text-slate-700">{formatCurrency(report.comparison.totalExpenses)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Balance {report.comparison.year}</div>
                  <div className={`text-xl font-bold ${report.comparison.netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(report.comparison.netBalance)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Monthly Detail Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Detalle Mensual</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Mes</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Ingresos</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Egresos</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Balance</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {report.monthlyBalance.map((month) => (
                    <tr key={month.month} className="border-t border-slate-100">
                      <td className="py-3 px-4 font-medium text-slate-700">{month.monthName}</td>
                      <td className="py-3 px-4 text-right text-emerald-600">{formatCurrency(month.income)}</td>
                      <td className="py-3 px-4 text-right text-red-600">{formatCurrency(month.expenses)}</td>
                      <td className={`py-3 px-4 text-right font-medium ${month.netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(month.netBalance)}
                      </td>
                      <td className={`py-3 px-4 text-right ${month.cumulativeBalance >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                        {formatCurrency(month.cumulativeBalance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-medium">
                  <tr>
                    <td className="py-3 px-4 text-slate-700">Total</td>
                    <td className="py-3 px-4 text-right text-emerald-600">{formatCurrency(report.summary.totalIncome)}</td>
                    <td className="py-3 px-4 text-right text-red-600">{formatCurrency(report.summary.totalExpenses)}</td>
                    <td className={`py-3 px-4 text-right ${report.summary.netBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(report.summary.netBalance)}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-500">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-slate-500">
          No se pudo cargar el reporte
        </div>
      )}
    </div>
  );
};
